import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { api, ApiError } from "../api/client.js";
import type { PersonaSummary } from "../api/types.js";

/**
 * Persona onboarding UX hardening (2026-09-12): superseded as the
 * Generate-readiness condition below — a persona with only 1-2 reference
 * images, or one still mid-training, is not actually usable for
 * Direct-Phota Generate. Kept only as a reference to the OLD threshold
 * this replaces, not used in `resolveOnboardingStep` anymore.
 */
const MINIMUM_REFERENCE_IMAGES_FOR_READY = 1;

interface OnboardingStatus {
  readonly canGenerateVideo: boolean;
  readonly licenseStatus: string | null;
  readonly planName: string | null;
}

type OnboardingStep = "verify_email" | "workspace" | "persona" | "license" | "ready" | "loading";

/**
 * FIX-009 (account-access/onboarding safety invariant): the gate's step
 * decision, extracted as a pure function — same "logic separable from
 * rendering, independently unit-testable" doctrine `resolveSelectedTenantId`
 * (AuthContext.ts) already established in this app; the dashboard's own
 * test setup (`vitest.config.ts`: `environment: "node"`, no jsdom/
 * testing-library) tests logic this way, never by rendering a component.
 *
 * `user === null` is NOT handled here — `OnboardingGate` itself renders
 * `children` unconditionally in that case, before this function is ever
 * called (that path is effectively unreachable in practice: this
 * component is only ever mounted inside `ProtectedShell`, which already
 * redirects an unauthenticated caller to `/login`).
 */
export function resolveOnboardingStep(input: {
  readonly isPlatformOwner: boolean;
  readonly emailVerified: boolean;
  readonly tenantId: string | null;
  readonly personas: readonly PersonaSummary[] | null;
  readonly onboardingStatus: OnboardingStatus | null;
}): OnboardingStep {
  // FIX-009: the platform OWNER is bootstrapped pre-verified and
  // unrestricted server-side (`requireVerifiedEmailForGenerate`/
  // `deriveEntitlements` both bypass on `isPlatformOwner`) — this gate
  // must never disagree and strand an OWNER whose legacy row predates
  // `email_verified_at` behind the same "verify your email" dead end a
  // real customer sees.
  if (input.isPlatformOwner) {
    return "ready";
  }
  if (!input.emailVerified) {
    return "verify_email";
  }
  if (input.tenantId === null) {
    return "workspace";
  }
  if (input.personas === null || input.onboardingStatus === null) {
    return "loading";
  }
  // Persona onboarding UX hardening (2026-09-12): Generate-readiness
  // means a real, trained Phota Profile — not merely "at least one
  // reference image." A persona still collecting references or
  // mid-training must not let onboarding claim this step is done.
  if (!input.personas.some((p) => p.photaReadinessState === "ready")) {
    return "persona";
  }
  if (!input.onboardingStatus.canGenerateVideo) {
    return "license";
  }
  return "ready";
}

/**
 * P2 remediation (GENERATE backlog follow-up review): mandatory
 * onboarding flow — SIGN UP -> VERIFY EMAIL -> CREATE/JOIN WORKSPACE ->
 * UPLOAD YOUR MODEL -> PERSONA READY -> GENERATE AVAILABLE. Deliberately
 * NOT a second Persona system: "persona ready" reads the SAME
 * `GET /videos/:tenantId/personas` (`activeReferenceCount`) GENERATE's
 * own PersonasTab already uses, and "license state" reads the SAME
 * `resolveTenantEntitlements` result every real paid submission is
 * checked against (`GET /videos/:tenantId/onboarding-status`). This
 * component only ever explains and gates navigation to the existing real
 * states — it introduces no new backend state of its own beyond the one
 * read-only onboarding-status endpoint.
 *
 * Wraps GENERATE's own content: renders the current step's explanation
 * instead of `children` until every precondition is met, exactly
 * matching the login-time ordering the product spec calls for (checked
 * in this same order every time the gate re-renders, not just at login).
 */
export function OnboardingGate({ tenantId, children }: { readonly tenantId: string | null; readonly children: React.ReactNode }) {
  const { user } = useAuth();
  const [personas, setPersonas] = useState<PersonaSummary[] | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId === null || user?.emailVerified !== true) {
      return;
    }
    setPersonas(null);
    setOnboardingStatus(null);
    setLoadError(null);
    Promise.all([
      api.get<{ personas: PersonaSummary[] }>(`/videos/${encodeURIComponent(tenantId)}/personas`),
      api.get<OnboardingStatus>(`/videos/${encodeURIComponent(tenantId)}/onboarding-status`).catch(() => null),
    ])
      .then(([personasResult, statusResult]) => {
        setPersonas(personasResult.personas);
        // A deployment without Phase D licensing wired (404) has no license
        // concept at all — never block onboarding on a feature that isn't
        // configured; `canGenerateVideo: true` is the honest default here.
        setOnboardingStatus(statusResult ?? { canGenerateVideo: true, licenseStatus: null, planName: null });
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : "Could not load your workspace's readiness state."));
  }, [tenantId, user?.emailVerified]);

  if (user === null) {
    return <>{children}</>;
  }

  const step = resolveOnboardingStep({ isPlatformOwner: user.isPlatformOwner, emailVerified: user.emailVerified, tenantId, personas, onboardingStatus });

  if (step === "ready") {
    return <>{children}</>;
  }

  if (step === "loading") {
    // Still loading (or the load failed) — show a lightweight loading
    // state rather than flashing "ready" content that might get yanked
    // back to an onboarding step a moment later.
    if (loadError !== null) {
      return <div className="error-banner">{loadError}</div>;
    }
    return <div className="empty-state">Checking your workspace's readiness…</div>;
  }

  return (
    <div className="card">
      <h2>Get set up for GENERATE</h2>
      <ol className="onboarding-steps">
        <li className={user.emailVerified ? "done" : "current"}>Verify your email</li>
        <li className={tenantId !== null ? (user.emailVerified ? "done" : "") : "current"}>Create or join a workspace</li>
        <li className={step === "persona" ? "current" : personas !== null && personas.some((p) => p.photaReadinessState === "ready") ? "done" : ""}>
          Upload your model (a Persona with a completed, trained Phota Profile)
        </li>
        <li className={step === "license" ? "current" : ""}>Generate available (an active license/plan)</li>
      </ol>
      {step === "verify_email" && (
        <p>
          Your account's email address hasn't been verified yet. Email delivery isn't configured in this environment, so no verification email is actually
          sent automatically — visit <a href="/resend-verification">this page</a> for details, or contact an operator to verify your account.
        </p>
      )}
      {step === "workspace" && (
        <p>
          You don't belong to a workspace yet. <a href="/workspaces/new">Create one</a> or ask a workspace ADMIN to invite you.
        </p>
      )}
      {step === "persona" && (
        <p>
          Your workspace doesn't have a ready Persona yet — a Persona needs a completed, trained Phota Profile (5 uploaded reference photos, training
          automatic) before GENERATE is available. Go to{" "}
          <a href="/generate/personas">GENERATE → PERSONAS</a> to create one, then upload its reference photos.
        </p>
      )}
      {step === "license" && (
        <p>
          Your workspace's Persona is ready, but this workspace does not currently have an active GENERATE license
          {onboardingStatus?.planName ? ` (current plan: ${onboardingStatus.planName}, status: ${onboardingStatus.licenseStatus ?? "none"})` : ""}. Contact
          the platform OWNER to assign or activate a plan for this workspace.
        </p>
      )}
    </div>
  );
}
